// How a site documentation entry links to a customer, project, offer and invoice: the same code
// validates the links on the server (utils/siteDocs/links.js); test/siteDocs/links.test.js checks
// both copies are identical below this header.
//
// ---- shared ----

const LINK_FIELDS = ["project", "offer", "invoice"];

const idOf = value => {
  if (!value) return "";
  if (typeof value === "object" && value._id) return String(value._id);
  return String(value);
};

const byId = list => new Map((list || []).map(item => [idOf(item), item]));

/**
 * Catalog lookups: `catalog` is { projects, offers, invoices }, each item carrying `_id`,
 * `client` and (offers, invoices) `project`; invoices also `offer`. An offer or invoice without
 * a customer belongs to its project's customer.
 */
const makeLookup = catalog => {
  const maps = {
    project: byId(catalog?.projects),
    offer: byId(catalog?.offers),
    invoice: byId(catalog?.invoices)
  };
  const get = (field, id) => (id ? maps[field].get(idOf(id)) || null : null);
  const ownerOf = (field, doc) => {
    if (!doc) return "";
    if (idOf(doc.client)) return idOf(doc.client);
    if (field !== "project" && doc.project) return idOf(get("project", doc.project)?.client);
    return "";
  };
  return { get, ownerOf };
};

/** Whether a document fits the chosen customer and project (empty choices fit everything). */
const fits = (lookup, field, doc, { client, project }) => {
  const owner = lookup.ownerOf(field, doc);
  if (client && owner && owner !== idOf(client)) return false;
  // A customer is chosen: documents of nobody are not offered, they would not belong to them
  if (client && !owner) return false;
  if (field !== "project" && project && doc.project && idOf(doc.project) !== idOf(project)) {
    return false;
  }
  return true;
};

/** The projects, offers and invoices to offer for the current choice. */
const linkOptions = (selection, catalog) => {
  const lookup = makeLookup(catalog);
  const choice = { client: idOf(selection?.client), project: idOf(selection?.project) };
  return {
    projects: (catalog?.projects || []).filter(doc =>
      fits(lookup, "project", doc, { client: choice.client })
    ),
    offers: (catalog?.offers || []).filter(doc => fits(lookup, "offer", doc, choice)),
    invoices: (catalog?.invoices || []).filter(doc => fits(lookup, "invoice", doc, choice))
  };
};

/**
 * The selection after one field changed: picking a project, offer or invoice fills in the
 * customer (and the project or offer it belongs to) when those are empty; links that no longer
 * fit the customer or project are cleared.
 */
const applyChange = (selection, field, value, catalog) => {
  const lookup = makeLookup(catalog);
  const next = {
    client: idOf(selection?.client),
    clientBranch: idOf(selection?.clientBranch),
    project: idOf(selection?.project),
    offer: idOf(selection?.offer),
    invoice: idOf(selection?.invoice)
  };
  next[field] = idOf(value);
  // A branch belongs to its customer; the caller sets the new one after a customer change
  if (field === "client" && next.client !== idOf(selection?.client)) next.clientBranch = "";

  const picked = LINK_FIELDS.includes(field) ? lookup.get(field, next[field]) : null;
  if (picked) {
    const owner = lookup.ownerOf(field, picked);
    if (owner && owner !== next.client) {
      next.client = owner;
      next.clientBranch = "";
    }
    if (field !== "project" && picked.project) next.project = idOf(picked.project);
    if (field === "invoice" && picked.offer && !next.offer && lookup.get("offer", picked.offer)) {
      next.offer = idOf(picked.offer);
    }
  }

  // Drop what no longer fits, project first (offers and invoices are checked against it)
  for (const name of LINK_FIELDS) {
    if (name === field || !next[name]) continue;
    const doc = lookup.get(name, next[name]);
    if (!doc || !fits(lookup, name, doc, next)) next[name] = "";
  }
  return next;
};

/**
 * Server-side check of a selection against the stored documents (the catalog holds only the
 * documents of this company). Returns { client, project, offer, invoice } with the customer,
 * project and offer filled in from the documents, or { error } naming the field that does not
 * fit.
 */
const resolveLinks = (selection, catalog) => {
  const lookup = makeLookup(catalog);
  const out = {
    client: idOf(selection?.client),
    project: idOf(selection?.project),
    offer: idOf(selection?.offer),
    invoice: idOf(selection?.invoice)
  };
  for (const field of LINK_FIELDS) {
    if (out[field] && !lookup.get(field, out[field])) return { error: field, reason: "missing" };
  }
  if (!out.client) {
    for (const field of ["invoice", "offer", "project"]) {
      const owner = lookup.ownerOf(field, lookup.get(field, out[field]));
      if (owner) {
        out.client = owner;
        break;
      }
    }
  }
  // Like the dropdowns: an offer or invoice brings its project, an invoice its offer
  for (const field of ["invoice", "offer"]) {
    const doc = lookup.get(field, out[field]);
    const owner = lookup.ownerOf(field, doc);
    // A document of another customer is reported below, not used to fill in
    if (!doc || (out.client && owner && owner !== out.client)) continue;
    if (doc.project && !out.project && lookup.get("project", doc.project)) {
      out.project = idOf(doc.project);
    }
    if (field === "invoice" && doc.offer && !out.offer && lookup.get("offer", doc.offer)) {
      out.offer = idOf(doc.offer);
    }
  }
  for (const field of LINK_FIELDS) {
    const doc = lookup.get(field, out[field]);
    if (!doc) continue;
    const owner = lookup.ownerOf(field, doc);
    if (out.client && owner && owner !== out.client) return { error: field, reason: "customer" };
    if (field !== "project" && out.project && doc.project && idOf(doc.project) !== out.project) {
      return { error: field, reason: "project" };
    }
  }
  return out;
};

// ---- end shared ----

export { LINK_FIELDS, idOf, linkOptions, applyChange, resolveLinks };
